import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useSession } from '@/context/SessionContext';
import { getCartForUser, updateCartItem, removeCartItem, clearCart, addCartItem } from '@/lib/supabase/carts';
import { getOrdersByUserId } from '@/lib/supabase/orders';
import { getProductsByStoreId } from '@/lib/supabase/products';
import { getStoreById } from '@/lib/supabase/stores';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Link } from 'react-router-dom';
import { checkoutCart } from '@/lib/supabase/carts';
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { usePlayBeep } from '@/hooks/usePlayBeep';
import { useVibrate } from '@/hooks/useVibrate';
import { format } from 'date-fns';

export default function CartSidebar({ storeId }: { storeId?: string }) {
  const { session } = useSession();
  const [cart, setCart] = useState<any | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [suggestedProducts, setSuggestedProducts] = useState<any[]>([]);
  const [scrollPos, setScrollPos] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const playBeep = usePlayBeep();
  const vibrate = useVibrate();
  
  const STORE_ID = '30e79df7-81e8-4c5f-a825-602e5899d961'; // Minibid 24hs

  const cartItemSorter = useMemo(
    () => new Intl.Collator('es', { sensitivity: 'base', numeric: true }),
    []
  );

  const sortedCartItems = useMemo(() => {
    const items = cart?.cart_items || [];
    if (!Array.isArray(items) || items.length <= 1) return items;

    return [...items].sort((a: any, b: any) => {
      const aTitle = String(a?.products?.title || '').trim();
      const bTitle = String(b?.products?.title || '').trim();
      const byTitle = cartItemSorter.compare(aTitle, bTitle);
      if (byTitle !== 0) return byTitle;

      const aProductId = String(a?.product_id || a?.products?.id || '').trim();
      const bProductId = String(b?.product_id || b?.products?.id || '').trim();
      const byProductId = cartItemSorter.compare(aProductId, bProductId);
      if (byProductId !== 0) return byProductId;

      return cartItemSorter.compare(String(a?.id || ''), String(b?.id || ''));
    });
  }, [cart?.cart_items, cartItemSorter]);
  

  const load = useCallback(async () => {
    if (!session?.user?.id) return;
    setLoading(true);
    try {
      const c = await getCartForUser(session.user.id, storeId || STORE_ID);
      setCart(c);
      
      // Load previous orders
      const o = await getOrdersByUserId(session.user.id);
      setOrders(o || []);
      
      // Load all products for suggestions
      const products = await getProductsByStoreId(storeId || STORE_ID);
      setAllProducts(products || []);
      
      // Generate suggestions based on cart content
      if (c?.cart_items?.length > 0) {
        generateSuggestions(c.cart_items, products || []);
      }
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [session?.user?.id, storeId]);

  // Función inteligente para generar sugerencias
  const generateSuggestions = (cartItems: any[], allProducts: any[]) => {
    // 1. Extraer palabras clave de los productos en el canasto
    const cartProductIds = new Set(cartItems.map((it: any) => it.product_id));
    const keywords = new Set<string>();
    
    cartItems.forEach((item: any) => {
      const title = item.products?.title || '';
      const words = title.toLowerCase().split(/[\s\-_]+/).filter((w: string) => w.length > 3);
      words.forEach((w: string) => keywords.add(w));
    });

    // 2. Filtrar productos que no estén en el canasto y que coincidan con keywords
    const scored = allProducts
      .filter((p: any) => !cartProductIds.has(p.id))
      .map((product: any) => {
        const title = product.title?.toLowerCase() || '';
        let score = 0;
        
        // Bonus por coincidir palabras clave
        keywords.forEach((keyword: string) => {
          if (title.includes(keyword)) score += 2;
        });
        
        // Bonus si tiene stock
        if (product.stock && product.stock > 0) score += 1;
        
        return { ...product, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 10); // Tomar top 10

    setSuggestedProducts(scored);
  };

  const scrollCarousel = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const scrollAmount = 250;
      const newPos = direction === 'left' 
        ? Math.max(0, scrollPos - scrollAmount)
        : scrollPos + scrollAmount;
      
      carouselRef.current.scrollLeft = newPos;
      setScrollPos(newPos);
    }
  };

  const addSuggestedToCart = async (product: any) => {
    if (!session?.user?.id) {
      toast({
        title: '⚠️ Inicia sesión',
        description: 'Necesitas estar logueado para agregar al canasto',
        variant: 'destructive',
      });
      return;
    }
    
    vibrate('add');
    playBeep('add');
    
    try {
      await addCartItem(session.user.id, product.id, 1);
      await load();
      window.dispatchEvent(new CustomEvent('cart-updated'));
      toast({
        title: '✅ Agregado',
        description: `${product.title} agregado al canasto`,
      });
    } catch (e) {
      toast({
        title: '❌ Error',
        description: (e as any)?.message || 'No se pudo agregar',
        variant: 'destructive',
      });
    }
  };

  useEffect(()=>{ load(); }, [load]);

  // Listen for cart updates from ProductSidebar or other sources
  useEffect(() => {
    const handleCartUpdate = () => {
      load();
    };

    window.addEventListener('cart-updated', handleCartUpdate);
    return () => {
      window.removeEventListener('cart-updated', handleCartUpdate);
    };
  }, [load]);

  // Check if any cart item has insufficient stock
  const hasStockIssues = cart?.cart_items?.some((it: any) => {
    const productStock = it.products?.stock;
    return productStock !== null && productStock !== undefined && it.quantity > productStock;
  });

  if (!session) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center w-full">
          <div className="font-semibold">Canasto</div>
        </div>
      </CardHeader>
      <CardContent>
        {(!cart || !cart.cart_items || cart.cart_items.length === 0) ? (
          <div className="text-sm text-muted-foreground">Canasto vacío</div>
        ) : (
          <div className="space-y-2">
            {sortedCartItems.map((it: any) => {
              const productStock = it.products?.stock;
              const hasStock = productStock !== null && productStock !== undefined;
              const insufficientStock = hasStock && it.quantity > productStock;
              
              return (
                <div key={it.id} className={`flex flex-col p-2 border rounded ${insufficientStock ? 'border-error bg-error-muted' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{it.products?.title || it.product_id}</div>
                      <div className="text-xs text-muted-foreground">
                        AR$ {it.unit_price} · 
                        <input
                          type="number"
                          inputMode="numeric"
                          min="0"
                          value={it.quantity}
                          onChange={async (e) => {
                            const newQty = parseInt(e.target.value) || 0;
                            if (newQty !== it.quantity) {
                              const type = newQty > it.quantity ? 'add' : 'decrease';
                              playBeep(type);
                              try {
                                await updateCartItem(it.id, newQty);
                                await load();
                                try { window.dispatchEvent(new CustomEvent('cart-updated')); } catch (_) { }
                              } catch (e) {
                                toast({ title: 'Error', description: (e as any)?.message || '', variant: 'destructive' });
                              }
                            }
                          }}
                          className="w-12 px-1 border rounded text-center bg-background dark:bg-slate-950 text-xs"
                        />
                        <span>u</span>
                        {hasStock && (
                          <span className={insufficientStock ? 'text-red-600 font-medium ml-2' : 'ml-2'}>
                            · Stock: {productStock}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        className="text-xs px-2 py-1 border rounded" 
                        onClick={async()=>{ 
                          playBeep('decrease');
                          try{ 
                            await updateCartItem(it.id, Math.max(0, it.quantity - 1)); 
                            await load(); 
                            try{ window.dispatchEvent(new CustomEvent('cart-updated')); }catch(_){ } 
                          }catch(e){ 
                            toast({ title: 'Error', description: (e as any)?.message || '', variant: 'destructive' }); 
                          } 
                        }}
                      >
                        -
                      </button>
                      <button 
                        className="text-xs px-2 py-1 border rounded" 
                        onClick={async()=>{ 
                          // Check if adding would exceed stock
                          if (hasStock && it.quantity + 1 > productStock) {
                            toast({ 
                              title: 'Stock insuficiente', 
                              description: `Solo hay ${productStock} unidades disponibles`, 
                              variant: 'destructive' 
                            });
                            return;
                          }
                          playBeep('add');
                          try{ 
                            await updateCartItem(it.id, it.quantity + 1); 
                            await load(); 
                            try{ window.dispatchEvent(new CustomEvent('cart-updated')); }catch(_){ } 
                          }catch(e){ 
                            toast({ title: 'Error', description: (e as any)?.message || '', variant: 'destructive' }); 
                          } 
                        }}
                      >
                        +
                      </button>
                      <button 
                        className="text-xs px-2 py-1 border rounded text-red-600" 
                        onClick={async()=>{ 
                          playBeep('decrease');
                          try{ 
                            await updateCartItem(it.id, 0); 
                            await load(); 
                            try{ window.dispatchEvent(new CustomEvent('cart-updated')); }catch(_){ } 
                          }catch(e){ 
                            toast({ title: 'Error', description: (e as any)?.message || '', variant: 'destructive' }); 
                          } 
                        }}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                  {insufficientStock && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-red-600">
                      <AlertTriangle className="w-3 h-3" />
                      <span>Stock insuficiente. Reduce la cantidad para continuar.</span>
                    </div>
                  )}
                </div>
              );
            })}

            {hasStockIssues && (
              <div className="flex items-center gap-2 p-2 bg-error-muted border border-error rounded text-sm text-error">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Algunos productos tienen stock insuficiente. Ajusta las cantidades antes de continuar.</span>
              </div>
            )}

            {/* Carrusel de sugerencias */}
            {suggestedProducts.length > 0 && (
              <div className="border-t pt-4 mt-4">
                <div className="text-sm font-semibold mb-3">Sugerencias para ti</div>
                <div className="relative">
                  <div
                    ref={carouselRef}
                    className="flex gap-2 overflow-x-auto scroll-smooth pb-2"
                    style={{ scrollBehavior: 'smooth' }}
                  >
                    {suggestedProducts.map((product: any) => (
                      <div
                        key={product.id}
                        className="flex-shrink-0 w-24 sm:w-28 max-w-[7rem] min-w-0 border rounded-lg p-1.5 bg-muted/70 hover:bg-muted transition-colors flex flex-col items-center shadow-sm"
                        style={{ boxSizing: 'border-box' }}
                      >
                        <div className="mb-1 w-full flex justify-center">
                          <div className="aspect-square w-14 sm:w-16 bg-gradient-to-br from-primary/10 to-primary/5 rounded flex items-center justify-center text-primary/20 text-xl">
                            {product.title?.charAt(0) || '📦'}
                          </div>
                        </div>
                        <div className="text-xs font-medium truncate mb-0.5 w-full text-center" title={product.title}>{product.title}</div>
                        <div className="text-xs text-muted-foreground mb-1 w-full text-center">
                          AR$ {product.fixed_sale_price || 0}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => addSuggestedToCart(product)}
                          className="w-full text-xs py-1 px-0"
                        >
                          Agregar
                        </Button>
                      </div>
                    ))}
                  </div>
                  {suggestedProducts.length > 4 && (
                    <>
                      <button
                        onClick={() => scrollCarousel('left')}
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 p-1 rounded hover:bg-background"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => scrollCarousel('right')}
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 p-1 rounded hover:bg-background"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <span className="text-base font-semibold">Total</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold">AR$ {cart?.cart_items?.reduce((acc: number, it: any) => acc + (it.unit_price * it.quantity), 0) || 0}</span>
                  {cart?.cart_items?.length > 0 && (
                    <div className="inline-flex items-center rounded-full bg-primary text-primary-foreground px-2 py-0.5 text-xs font-semibold ml-1">
                      {cart.cart_items.length}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <Link to="/checkout" className="text-sm underline">Ir a checkout</Link>
                <Button 
                  onClick={async()=>{
                    try {
                      const res = await checkoutCart(session.user.id, storeId);
                      const orderRow = Array.isArray(res) ? res[0] : (res?.order || res);
                      const orderId = orderRow?.order_id || orderRow?.id;
                      const orderNumber = orderRow?.order_number;
                      let friendlyOrder = '';
                      if (orderNumber) {
                        friendlyOrder = `#${orderNumber}`;
                      } else if (orderId) {
                        friendlyOrder = `#${String(orderId).slice(0,8)}`;
                      }
                      toast({ title: 'Orden creada', description: friendlyOrder ? `Orden ${friendlyOrder} creada` : 'Orden creada' });
                      await load(); 
                      try{ window.dispatchEvent(new CustomEvent('cart-updated')); }catch(_){ }
                    } catch(e) {
                      toast({ title: 'Error', description: (e as any)?.message || '', variant: 'destructive' });
                    }
                  }}
                  disabled={hasStockIssues || loading}
                >
                  Checkout
                </Button>
              </div>
            </div>
                  {/* Sugeridos tipo favoritos */}
                  {suggestedProducts.length > 0 && (
                    <div className="mt-6">
                      <div className="text-sm font-semibold mb-3">Sugerencias para ti</div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {suggestedProducts.slice(0, 3).map((product: any) => (
                          <div key={product.id} className="rounded-lg border bg-card text-card-foreground shadow-sm flex flex-col items-center p-3">
                            <div className="w-16 h-16 rounded bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center text-primary/30 text-2xl mb-2">
                              {product.title?.charAt(0) || '📦'}
                            </div>
                            <div className="text-xs font-medium truncate mb-1 w-full text-center" title={product.title}>{product.title}</div>
                            <div className="text-xs text-muted-foreground mb-2 w-full text-center">AR$ {product.fixed_sale_price || 0}</div>
                            <Button size="sm" onClick={() => addSuggestedToCart(product)} className="w-full text-xs py-1 px-0">Agregar</Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
          </div>
        )}
        
        {/* Separator and Previous Orders */}
        {cart?.cart_items && cart.cart_items.length > 0 && orders.length > 0 && (
          <div className="border-t my-4 pt-4">
            <div className="text-sm font-semibold text-muted-foreground mb-3">Pedidos anteriores</div>
            <div className="space-y-2">
              {orders.map((order: any) => (
                <div key={order.id} className="p-2 border rounded text-xs space-y-1">
                  <div className="flex justify-between items-start">
                    <div className="font-medium truncate">
                      {order.stores?.name || (order.order_items?.[0]?.products?.product_type === 'tours' ? 'Minibid 30e79df7-81e8-4c5f-a825-602e5899d961' : 'Orden')}
                    </div>
                    <span className="text-muted-foreground whitespace-nowrap ml-2">#{order.order_number || order.id?.slice(0, 8)}</span>
                  </div>
                  <div className="text-muted-foreground">
                    {order.created_at ? format(new Date(order.created_at), 'dd/MM/yyyy HH:mm') : '—'}
                  </div>
                  <div className="text-muted-foreground">
                    {order.order_items?.length || 0} item{order.order_items?.length !== 1 ? 's' : ''}
                  </div>
                  <div className="font-medium pt-1">
                    AR$ {Number(order.total || 0).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


